import { Link } from 'react-router-dom';
import {
  ArrowsRightLeftIcon,
  BeakerIcon,
  BookOpenIcon,
  ChatBubbleLeftEllipsisIcon,
  FolderIcon,
  MicrophoneIcon,
  ShareIcon,
  SparklesIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';

interface PromptExampleProps {
  bad: string;
  good: string;
  why: string;
}

function PromptExample({ bad, good, why }: PromptExampleProps) {
  return (
    <div className="grid gap-3 md:grid-cols-3">
      <div className="rounded-lg border border-red-200 bg-red-50 p-3 dark:border-red-900 dark:bg-red-950/30">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-red-600 dark:text-red-400">Too vague</p>
        <p className="text-sm text-foreground">{bad}</p>
      </div>
      <div className="rounded-lg border border-green-200 bg-green-50 p-3 dark:border-green-900 dark:bg-green-950/30">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-green-600 dark:text-green-400">Better</p>
        <p className="text-sm text-foreground">{good}</p>
      </div>
      <div className="rounded-lg border border-border bg-muted/40 p-3">
        <p className="mb-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">Why</p>
        <p className="text-sm text-foreground">{why}</p>
      </div>
    </div>
  );
}

export default function OrbiHelp() {
  return (
    <div className="mx-auto max-w-4xl space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="text-sm font-semibold uppercase tracking-wide text-foreground">How to use Orbi</h1>
        <Link to="/orbi" className="text-xs font-medium text-primary hover:text-primary">
          Open Orbi →
        </Link>
      </div>

      {/* Intro */}
      <section className="rounded-2xl border border-border bg-card p-6">
        <h2 className="text-lg font-semibold text-foreground">Talking to Orbi</h2>
        <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
          Orbi is Planbase's AI design assistant. You describe what you want, Orbi generates
          concepts, drawings, and plans you can iterate on. There are three ways to talk to it,
          selected by the chip at the top of the composer.
        </p>
      </section>

      {/* Modes */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">The three modes</h2>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-2 flex items-center gap-2">
            <BeakerIcon className="h-5 w-5 text-primary" />
            <p className="text-sm font-semibold text-foreground">Workflow</p>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            The generative canvas. Type a brief and Orbi drops the first design as a node.
            Click the connector on the right edge of any node to spawn the next step —
            Floor Plan, Site Plan, Elevation, Section, Presentation, Working Drawing, or Interior
            Render. Every child references its parent visually so the same building carries through
            the chain.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-2 flex items-center gap-2">
            <BookOpenIcon className="h-5 w-5 text-primary" />
            <p className="text-sm font-semibold text-foreground">Research</p>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            A conversation for questions and context. Orbi returns architectural briefs, comparisons,
            programme notes, precedent guidance, or planning summaries. Uses the full
            Architect's Data reference behind the scenes.
          </p>
        </div>

        <div className="rounded-2xl border border-border bg-card p-5">
          <div className="mb-2 flex items-center gap-2">
            <FolderIcon className="h-5 w-5 text-primary" />
            <p className="text-sm font-semibold text-foreground">Portfolio</p>
          </div>
          <p className="text-sm leading-relaxed text-muted-foreground">
            Orbi writes about your own projects — descriptions, rationales, presentation copy —
            using the projects already in your Portfolio as context. Use it when a project is
            uploaded and you need better copy without leaving the app.
          </p>
        </div>
      </section>

      {/* Prompt examples */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Writing better prompts</h2>
        <p className="text-sm leading-relaxed text-muted-foreground">
          Diffusion models reward specificity. A vague prompt gets a stock building; a specific
          brief keeps every node in the same design.
        </p>

        <PromptExample
          bad="A house"
          good="Two-story timber cottage, pitched slate roof, three bedrooms, dry-stone base"
          why="Materials, storeys, roof form and room count anchor the whole workflow. Later nodes stay recognisably the same building."
        />
        <PromptExample
          bad="Office"
          good="Adaptive reuse of a Victorian warehouse into 20-desk open studio, exposed brick, rooflight cluster"
          why="Constraints (adaptive reuse, existing brick) plus a specific programme give Orbi a real design problem to solve."
        />
        <PromptExample
          bad="Nice render"
          good="Presentation render, dusk exterior, warm interior glow, low camera facing north corner"
          why="Camera position, lighting mood and time of day compose the shot for you rather than leaving them to chance."
        />
      </section>

      {/* Workflow tips */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="text-base font-semibold text-foreground">Working in the Workflow canvas</h2>
        <ul className="mt-3 space-y-3 text-sm leading-relaxed text-foreground">
          <li className="flex gap-3">
            <SparklesIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <span>
              <strong className="text-foreground">Start with a Concept.</strong> The first prompt sets
              the "root brief" that every following node references. Spend a moment being specific here.
            </span>
          </li>
          <li className="flex gap-3">
            <ArrowsRightLeftIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <span>
              <strong className="text-foreground">Chain by connector, not composer.</strong> To get the
              floor plan of the concept you just made, click the orange connector on the concept's
              right edge → Floor Plan. Orbi will use the concept image as a visual reference so the
              floor plan matches the same building.
            </span>
          </li>
          <li className="flex gap-3">
            <ShareIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <span>
              <strong className="text-foreground">Publish when a chain is complete.</strong> Header
              Share → <em>Export to Planverse</em> puts your workflow on the community feed.
              <em> Publish</em> creates a shareable link without listing it. <em>Get link</em>
              copies the canvas URL for private collaborators.
            </span>
          </li>
          <li className="flex gap-3">
            <MicrophoneIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <span>
              <strong className="text-foreground">Voice input.</strong> The mic in the composer uses
              your browser's speech recognition. Great for long, spoken briefs — say "3-bedroom
              townhouse with a pitched roof, timber cladding on the upper floor and stone below"
              and hit send.
            </span>
          </li>
          <li className="flex gap-3">
            <ChatBubbleLeftEllipsisIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <span>
              <strong className="text-foreground">Refine with a follow-up prompt.</strong> After
              generating, type an adjustment ("make it warmer", "add a courtyard") and send —
              Orbi appends it to the workflow brief and produces a refined child.
            </span>
          </li>
          <li className="flex gap-3">
            <TrashIcon className="mt-0.5 h-4 w-4 flex-shrink-0 text-muted-foreground" />
            <span>
              <strong className="text-foreground">Managing chats.</strong> Recent chats live in the
              left sidebar. Hover a chat to see the trash icon — click once for the red confirmation
              state, click again within 3s to delete.
            </span>
          </li>
        </ul>
      </section>

      {/* Node types */}
      <section className="space-y-3">
        <h2 className="text-base font-semibold text-foreground">Node types in Workflow</h2>
        <div className="grid gap-2 md:grid-cols-2">
          {[
            ['Concept', 'The starting design — atmospheric render, sets the visual language.'],
            ['Floor Plan', 'Orthographic top-down drawing of the plan.'],
            ['Site Plan', 'Zoomed-out top-down showing building in landscape / context.'],
            ['Elevation', 'Straight-on facade study — great for material studies.'],
            ['Section', 'Cut-through view showing levels, structure, daylight.'],
            ['Presentation', 'High-fidelity render for pitches and portfolio.'],
            ['Working Drawing', 'Technical, dimensioned, precise line weights.'],
            ['Interior Render', 'A furnished room inside the same building.'],
          ].map(([label, desc]) => (
            <div key={label} className="rounded-lg border border-border bg-card p-3">
              <p className="text-sm font-medium text-foreground">{label}</p>
              <p className="mt-1 text-xs text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Prompt library */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="text-base font-semibold text-foreground">Prompt starters</h2>
        <p className="mt-2 text-sm text-muted-foreground">Copy-paste and adjust.</p>
        <ul className="mt-3 space-y-2 text-sm">
          {[
            'Compact 3-bedroom townhouse, brick and larch cladding, small courtyard to south, ~120 sqm',
            'Adaptive reuse of a 1930s warehouse into 40-desk co-working with mezzanine and rooflights',
            'Coastal single-family home, cross-laminated timber, cantilevered upper storey, framed sea view',
            'Two-storey primary school pavilion, cross-ventilated classrooms, courtyard learning garden',
            'Landscape masterplan for a 2-hectare park, wetland edge, boardwalk, pavilion café',
            'Micro-cabin, prefabricated, off-grid, one bedroom, panoramic north-facing glazing',
          ].map((p) => (
            <li key={p} className="rounded-md border border-border bg-background px-3 py-2 font-mono text-xs text-foreground">
              {p}
            </li>
          ))}
        </ul>
      </section>

      {/* Voice input tips */}
      <section className="rounded-2xl border border-border bg-card p-5">
        <h2 className="text-base font-semibold text-foreground">Voice input notes</h2>
        <ul className="mt-3 space-y-2 text-sm leading-relaxed text-muted-foreground">
          <li>· The mic button uses your browser's built-in speech recognition (works in Chrome, Edge, Safari).</li>
          <li>· Click once to start; a red pulsing stop button appears while listening. Click it or press Enter to stop and send.</li>
          <li>· Speak your brief in one go — Orbi handles longer prompts better than short ones.</li>
          <li>· You can edit the transcribed text before sending.</li>
        </ul>
      </section>
    </div>
  );
}
